;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:propstrata-app":true});}catch(e){}}();import {
	type AddWorkspaceDashboardViewSchema,
	addWorkspaceDashboardViewSchema,
	type BulkDeleteWorkspaceUsersSchema,
	bulkDeleteWorkspaceUsersSchema,
	type DeleteWorkspaceDashboardViewSchema,
	type DeleteWorkspaceUserSchema,
	deleteWorkspaceDashboardViewSchema,
	deleteWorkspaceUserSchema,
	type EditWorkspaceDashboardViewSchema,
	type EditWorkspaceSchema,
	type EditWorkspaceUserSchema,
	editWorkspaceDashboardViewSchema,
	editWorkspaceSchema,
	editWorkspaceUserSchema,
} from "@/lib/schema/workspace";
import { logger } from "@/lib/services/logger";
import { queryFetcher } from "@/queries/config/query-fetcher";
import type { ProviderType } from "@/types/dataProvider";

/**
 * Deletes a user from the specified workspace
 * @param {DeleteWorkspaceUserSchema} data - Properties
 * @returns
 */
export function deleteWorkspaceUser(data: DeleteWorkspaceUserSchema) {
	const validationResult = deleteWorkspaceUserSchema.safeParse(data);

	if (!validationResult.success) {
		logger.debug("Invalid deleteWorkspaceUser payload", {
			data,
			operation: "deleteWorkspaceUser",
		});

		throw new Error("Error deleteing user");
	}

	const { userId, workspaceId } = validationResult.data;

	return queryFetcher(
		`/api/workspaces/users/${userId}?workspaceId=${workspaceId}`,
		{
			method: "DELETE",
		},
	);
}

/**
 * Removes multiple users from the specified workspace
 */
export function bulkDeleteWorkspaceUsers(
	data: BulkDeleteWorkspaceUsersSchema,
): Promise<{ deletedCount: number }> {
	const validationResult = bulkDeleteWorkspaceUsersSchema.safeParse(data);

	if (!validationResult.success) {
		logger.debug("Invalid bulkDeleteWorkspaceUsers payload", {
			data,
			operation: "bulkDeleteWorkspaceUsers",
		});

		throw new Error("Error bulk deleting users from workspace");
	}

	const { workspaceId, workspaceUserIds } = validationResult.data;

	return queryFetcher<{ deletedCount: number }>(
		`/api/workspaces/${workspaceId}/members`,
		{
			body: JSON.stringify({ workspaceUserIds }),
			method: "DELETE",
		},
	);
}

/**
 * Edits a user information from the specified workspace
 * @param {EditWorkspaceUserSchema} data
 * @returns
 */
export function editWorkspaceUser(data: EditWorkspaceUserSchema) {
	const validationResult = editWorkspaceUserSchema.safeParse(data);

	if (!validationResult.success) {
		logger.debug("Invalid editWorkspaceUser payload", {
			data,
			operation: "editWorkspaceUser",
		});

		throw new Error("Error editing user");
	}

	const { userId, workspaceId, data: payload } = validationResult.data;

	return queryFetcher(
		`/api/workspaces/users/${userId}?workspaceId=${workspaceId}`,
		{
			body: JSON.stringify(payload),
			method: "PUT",
		},
	);
}

/**
 * Query to delete a workspace user invitation
 * @param {DeleteWorkspaceUserSchema} data
 * @returns
 */
export function deleteWorkspaceUserInvitation(data: DeleteWorkspaceUserSchema) {
	const validationResult = deleteWorkspaceUserSchema.safeParse(data);

	if (!validationResult.success) {
		logger.debug("Invalid deleteWorkspaceUserInvitation payload", {
			data,
			operation: "deleteWorkspaceUserInvitation",
		});

		throw new Error("Error deleting user invitation");
	}

	const { userId, workspaceId } = validationResult.data;

	return queryFetcher(`/api/workspaces/${workspaceId}/invitations/${userId}`, {
		method: "DELETE",
	});
}

/**
 * Query to edit a workspace
 * @param {EditWorkspaceSchema} data
 * @returns
 */
export function editWorkspace(data: EditWorkspaceSchema) {
	const validationResult = editWorkspaceSchema.safeParse(data);

	if (!validationResult.success) {
		logger.debug("Invalid editWorkspace payload", {
			data,
			operation: "editWorkspace",
		});

		throw new Error("Error editing workspace");
	}

	const { name, workspaceId } = validationResult.data;

	return queryFetcher(`/api/workspaces/${workspaceId}`, {
		body: JSON.stringify({ name }),
		method: "PUT",
	});
}

export async function resyncPropertiesData(
	workspaceId: string,
	providerType?: ProviderType,
): Promise<{ success: boolean }> {
	return queryFetcher(`/api/workspaces/${workspaceId}/properties-sync`, {
		body: JSON.stringify(providerType ? { providerType } : {}),
		method: "POST",
	});
}

export async function resyncPropertyStaffData(
	workspaceId: string,
	providerType?: ProviderType,
): Promise<{ success: boolean }> {
	return queryFetcher(`/api/workspaces/${workspaceId}/property-staff-sync`, {
		body: JSON.stringify(providerType ? { providerType } : {}),
		method: "POST",
	});
}

export async function resyncChartOfAccountsData(
	workspaceId: string,
): Promise<{ success: boolean }> {
	return queryFetcher(`/api/workspaces/${workspaceId}/chart-of-accounts-sync`, {
		method: "POST",
	});
}

export async function resyncUnitsData(
	workspaceId: string,
	providerType?: ProviderType,
): Promise<{ success: boolean }> {
	return queryFetcher(`/api/workspaces/${workspaceId}/units-sync`, {
		body: JSON.stringify(providerType ? { providerType } : {}),
		method: "POST",
	});
}

/**
 * Query to add a workspace dashboard view
 * @param {AddWorkspaceDashboardViewSchema} data
 * @returns
 */
export function addWorkspaceDashboardViewQuery(
	data: AddWorkspaceDashboardViewSchema,
) {
	const validationResult = addWorkspaceDashboardViewSchema.safeParse(data);

	if (!validationResult.success) {
		logger.debug("Invalid addWorkspaceDashboardViewQuery payload", {
			data,
			operation: "addWorkspaceDashboardViewQuery",
		});

		throw new Error("Error adding views");
	}

	const { views, workspaceId } = validationResult.data;

	return queryFetcher(`/api/workspaces/${workspaceId}/views`, {
		body: JSON.stringify({ views }),
		method: "POST",
	});
}

/**
 * Query to delete a workspace dashboard
 * @param {DeleteWorkspaceDashboardViewSchema} data
 * @returns
 */
export function deleteWorkspaceDashboardViewQuery(
	data: DeleteWorkspaceDashboardViewSchema,
) {
	const validationResult = deleteWorkspaceDashboardViewSchema.safeParse(data);

	if (!validationResult.success) {
		logger.debug("Invalid deleteWorkspaceDashboardViewQuery payload", {
			data,
			operation: "deleteWorkspaceDashboardViewQuery",
		});

		throw new Error("Error deleting views");
	}

	const { viewId, workspaceId } = validationResult.data;

	return queryFetcher(`/api/workspaces/${workspaceId}/views/${viewId}`, {
		method: "DELETE",
	});
}

/**
 * Query to edit a workspace dashboard view
 * @param {EditWorkspaceDashboardViewSchema} data
 * @returns
 */
export function editWorkspaceDashboardViewQuery(
	data: EditWorkspaceDashboardViewSchema,
) {
	const validationResult = editWorkspaceDashboardViewSchema.safeParse(data);

	if (!validationResult.success) {
		const errorMessage = "Error editing views";

		logger.debug("Invalid editWorkspaceDashboardViewQuery payload", {
			data,
			errorMessage,
			operation: "editWorkspaceDashboardViewQuery",
		});

		throw new Error(errorMessage);
	}

	const { viewId, workspaceId, ...payload } = validationResult.data;

	return queryFetcher(`/api/workspaces/${workspaceId}/views/${viewId}`, {
		body: JSON.stringify(payload),
		method: "PUT",
	});
}
